"use client";

import { useEffect } from "react";

type Side = "CT" | "T" | "Dragon";

const PAYOUT: Record<Side, number> = { CT: 2, T: 2, Dragon: 16 };
const GOLD_SRC = "/cdn-next/static/media/goldLogo.0f64ec69.avif";
const SILVER_SRC = "/cdn-next/static/media/silverLogo.777fc653.avif";
const DRAGON_SRC = "/cdn-next/static/media/dragonLogo.93c0cf1f.avif";

/* ── WebAudio sound engine (synthesized 1:1 style: ticks + win chime) ── */
function createSoundEngine(isMuted: () => boolean) {
  let ctx: AudioContext | null = null;
  const ac = (): AudioContext | null => {
    try {
      if (!ctx) {
        const AC =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext?: typeof AudioContext })
            .webkitAudioContext;
        if (!AC) return null;
        ctx = new AC();
      }
      if (ctx.state === "suspended") void ctx.resume();
      return ctx;
    } catch {
      return null;
    }
  };
  const tone = (
    type: OscillatorType,
    f0: number,
    f1: number,
    vol: number,
    dur: number,
    delay = 0
  ) => {
    try {
      if (isMuted()) return;
      const c = ac();
      if (!c) return;
      const t = c.currentTime + delay;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(Math.max(30, f0), t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(30, f1), t + dur);
      gain.gain.setValueAtTime(vol, t);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.02);
      osc.connect(gain);
      gain.connect(c.destination);
      osc.start(t);
      osc.stop(t + dur + 0.05);
    } catch {
      /* noop */
    }
  };
  return {
    tick: () => tone("square", 2200, 1400, 0.05, 0.05),
    lock: () => {
      tone("sine", 880, 880, 0.09, 0.1);
      tone("sine", 880, 880, 0.09, 0.1, 0.12);
    },
    win: () => {
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) =>
        tone("triangle", f, f, 0.13, 0.35, i * 0.09)
      );
    },
    lose: () => tone("sine", 220, 110, 0.07, 0.25),
  };
}

function sideFromHeader(header: Element): Side | null {
  const cls = header.className || "";
  const img = header.querySelector("img");
  const src = img?.getAttribute("src") || "";
  if (cls.includes("Roulette_cardGold") || src.includes("goldLogo")) return "T";
  if (cls.includes("Roulette_cardSilver") || src.includes("silverLogo"))
    return "CT";
  if (cls.includes("Roulette_cardCs") || src.includes("dragonLogo"))
    return "Dragon";
  const desc =
    header.parentElement?.querySelector(
      '[class*="Card_headerDesc"], [class*="CardForMobile_headerDesc"]'
    )?.textContent || "";
  if (desc.includes("16X")) return "Dragon";
  return null;
}

function pickWinner(): Side {
  const r = Math.random() * 100;
  if (r < 7) return "Dragon";
  if (r < 7 + 46) return "T";
  return "CT";
}

function randomHex(n: number): string {
  let s = "";
  const chars = "0123456789abcdef";
  for (let i = 0; i < n; i++)
    s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

/**
 * Demo roulette engine for the stolen /roulette snapshot.
 * Drives the captured absolute-positioned carousel 1:1 (same DOM, same
 * classes): idle drift while betting → locked roll with decelerating ticks
 * → winner centered under the pointer with glow + payout banner.
 * Place Bet headers + bet modifiers + bet input are all wired (demo bets,
 * nothing leaves the browser).
 */
export function RouletteClient() {
  useEffect(() => {
    const root = document.getElementById("verbatim-root");
    if (!root) return;
    // only on the roulette page (snapshot has the wheel stage)
    if (!root.querySelector("[data-card-type]")) return;

    const cleanups: Array<() => void> = [];
    const on = (
      el: Element | Document | Window | null,
      ev: string,
      fn: EventListener,
      opts?: AddEventListenerOptions
    ) => {
      if (!el) return;
      el.addEventListener(ev, fn, opts);
      cleanups.push(() => el.removeEventListener(ev, fn, opts));
    };

    let muted = false;
    try {
      muted = localStorage.getItem("roulette-muted") === "1";
    } catch {
      /* noop */
    }
    const sounds = createSoundEngine(() => muted);

    /* ── wheel cards ── */
    interface Card {
      wrap: HTMLElement;
      stage: HTMLElement;
      side: Side;
      x0: number;
      y0: number;
    }
    const stages = Array.from(
      root.querySelectorAll<HTMLElement>("[data-card-type]")
    );
    if (stages.length < 8) return;
    const cards: Card[] = [];
    for (const stage of stages) {
      const wrap = stage.parentElement as HTMLElement | null;
      if (!wrap) continue;
      const t = wrap.style.transform || "";
      const m = t.match(
        /translate3d\(\s*(-?[\d.]+)px,\s*(-?[\d.]+)px/i
      );
      if (!m) continue;
      const raw = (stage.getAttribute("data-card-type") || "").toLowerCase();
      const side: Side =
        raw.includes("gold") ? "T" : raw.includes("silver") ? "CT" : "Dragon";
      cards.push({
        wrap,
        stage,
        side,
        x0: parseFloat(m[1]),
        y0: parseFloat(m[2]),
      });
    }
    if (cards.length < 8) return;
    const viewport = (cards[0].wrap.parentElement as HTMLElement) || root;
    const sortedX = cards.map((c) => c.x0).sort((a, b) => a - b);
    const diffs: number[] = [];
    for (let i = 1; i < sortedX.length; i++)
      diffs.push(sortedX[i] - sortedX[i - 1]);
    diffs.sort((a, b) => a - b);
    const pitch = diffs.length
      ? diffs[Math.floor(diffs.length / 2)]
      : 132;
    const totalW = pitch * cards.length;
    const minX = sortedX[0] - pitch;
    const baseY =
      cards
        .map((c) => c.y0)
        .sort((a, b) => a - b)[Math.floor(cards.length / 2)] || 92;
    const centerX = (): number => (viewport.clientWidth || 900) / 2;

    let off = 0;
    const wrapPos = (x: number): number =>
      ((((x - minX) % totalW) + totalW) % totalW) + minX;
    const posOf = (c: Card): number => wrapPos(c.x0 - off);

    const paint = () => {
      const cx = centerX();
      let best: Card | null = null;
      let bestD = Infinity;
      for (const c of cards) {
        const d = Math.abs(posOf(c) + 63.5 - cx);
        if (d < bestD) {
          bestD = d;
          best = c;
        }
      }
      for (const c of cards) {
        const x = posOf(c);
        const dist = Math.abs(x + 63.5 - cx);
        const prox = Math.max(0, 1 - dist / (pitch * 3));
        const y = baseY - 8 * prox;
        c.wrap.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
        c.wrap.style.setProperty("--center-progress", prox.toFixed(5));
        c.wrap.style.setProperty(
          "--center-scale",
          (1 + 0.12 * prox).toFixed(5)
        );
        c.wrap.style.setProperty("--center-lift", `${(-8 * prox).toFixed(3)}px`);
        c.wrap.setAttribute("data-centered", c === best ? "true" : "false");
      }
      return best;
    };

    /* ── timer UI (captured "Rolling 3.6s" split-char spans) ── */
    const rollingLabel = Array.from(root.querySelectorAll("span")).find(
      (s) => (s.textContent || "").trim() === "Rolling"
    );
    const timerBox = rollingLabel?.parentElement as HTMLElement | null;
    const timerValue = timerBox?.children?.[1] as HTMLElement | undefined;
    const progressBar = timerBox?.parentElement?.parentElement?.querySelector(
      ".origin-left"
    ) as HTMLElement | null;
    const setTimer = (label: string, secs: number, frac: number) => {
      try {
        if (rollingLabel) rollingLabel.textContent = label;
        if (timerValue)
          timerValue.textContent = `${secs.toFixed(1)}s`;
        if (progressBar)
          progressBar.style.transform = `scaleX(${Math.max(
            0,
            Math.min(1, frac)
          )})`;
      } catch {
        /* noop */
      }
    };

    /* ── hash + last-100 ── */
    const hashEl = Array.from(root.querySelectorAll("p")).find((p) =>
      /^[0-9a-f]{64}$/.test((p.textContent || "").trim())
    );
    const last100Btns = Array.from(root.querySelectorAll("button")).filter(
      (b) =>
        b.getAttribute("aria-label") === "Open last 100 rounds breakdown"
    );

    /* ── banner + toast ── */
    const banner = document.createElement("div");
    banner.className = "roulette-demo-banner";
    banner.hidden = true;
    document.body.appendChild(banner);
    cleanups.push(() => banner.remove());
    let bannerTimer = 0;
    const showBanner = (html: string, ms = 4200) => {
      banner.innerHTML = html;
      banner.hidden = false;
      banner.classList.remove("roulette-demo-in");
      void banner.offsetWidth;
      banner.classList.add("roulette-demo-in");
      window.clearTimeout(bannerTimer);
      bannerTimer = window.setTimeout(() => {
        banner.hidden = true;
      }, ms);
      cleanups.push(() => window.clearTimeout(bannerTimer));
    };

    /* ── demo bets ── */
    const bets: Record<Side, number> = { CT: 0, T: 0, Dragon: 0 };
    const betInput = root.querySelector<HTMLInputElement>(
      ".Input_inputField__Rtexm"
    );
    const readBet = (): number => {
      const v = Number(betInput?.value);
      if (!Number.isFinite(v) || v <= 0) return 10;
      return Math.min(10000, Math.max(0.1, Math.round(v * 100) / 100));
    };

    const cardBodies: Partial<Record<Side, HTMLElement[]>> = {
      CT: [],
      T: [],
      Dragon: [],
    };
    const baseTotals: Partial<Record<Side, number>> = {};
    const wireBetCards = () => {
      const headers = Array.from(
        root.querySelectorAll(
          ".Card_header__nyOYy, .CardForMobile_headerMobile__J1k74"
        )
      );
      for (const h of headers) {
        const side = sideFromHeader(h);
        if (!side) continue;
        const main =
          (h.closest(".Card_main__Uue17") as HTMLElement | null) ||
          (h.closest(".CardForMobile_main__Hn1A4") as HTMLElement | null);
        const body =
          main?.querySelector<HTMLElement>(
            ".Card_cardBody__MwSNs, [class*='CardForMobile_body']"
          ) ||
          (main?.parentElement?.querySelector<HTMLElement>(
            ".Card_cardBody__MwSNs"
          ) as HTMLElement | null);
        if (body && !cardBodies[side]!.includes(body))
          cardBodies[side]!.push(body);
        if (main && !(main as HTMLElement).dataset.rouletteSide) {
          (main as HTMLElement).dataset.rouletteSide = side;
          const totalEl = main.querySelector(".Card_amount___aD_Y");
          if (totalEl && baseTotals[side] === undefined) {
            const n = parseFloat(
              (totalEl.textContent || "").replace(/[^0-9.]/g, "")
            );
            baseTotals[side] = Number.isFinite(n) ? n : 0;
          }
        }
      }
    };
    wireBetCards();

    const refreshTotals = () => {
      for (const side of ["CT", "T", "Dragon"] as Side[]) {
        const mains = Array.from(
          root.querySelectorAll<HTMLElement>("[data-roulette-side]")
        ).filter((m) => m.dataset.rouletteSide === side);
        const total = (baseTotals[side] || 0) + bets[side];
        for (const main of mains) {
          const totalEl = main.querySelector(".Card_amount___aD_Y");
          if (totalEl)
            totalEl.textContent = total.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            });
        }
      }
    };

    let phase: "betting" | "rolling" | "result" = "betting";
    const placeBet = (side: Side) => {
      if (phase !== "betting") {
        sounds.lose();
        showBanner(
          `<b>Bets locked</b><span>Wait for the next round to place your bet.</span>`,
          2200
        );
        return;
      }
      const amount = readBet();
      bets[side] += Math.round(amount * 100) / 100;
      // render a demo row at the top of that side's card body
      for (const body of cardBodies[side] || []) {
        try {
          const tpl = body.querySelector(
            ".Card_row__fcBQV"
          ) as HTMLElement | null;
          const row = document.createElement("div");
          row.className =
            tpl?.className ||
            "Card_row__fcBQV Card_rowEnter__9VDKg px-[10px] roulette-demo-row";
          row.setAttribute("data-demo-bet", "you");
          row.style.marginTop = "18px";
          row.innerHTML =
            `<div class="Card_rowLeft__sZPSl" style="display:flex;align-items:center;gap:8px;">` +
            `<img alt="avatar" src="/images/new-default-avatar.svg" width="32" height="32" style="border-radius:32px;width:32px;height:32px;object-fit:cover;" />` +
            `<span style="color:#fff;font-size:13px;font-weight:600;">You</span></div>` +
            `<div class="Card_rowRight__kZyXj" style="display:flex;align-items:center;gap:6px;">` +
            `<img alt="coin" src="/images/coin-2.avif" width="18" height="18" />` +
            `<span style="color:#fff;font-size:14px;font-weight:600;">${amount.toLocaleString(
              "en-US",
              { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}</span></div>`;
          body.prepend(row);
        } catch {
          /* noop */
        }
      }
      refreshTotals();
      showBanner(
        `<b>${amount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} bet on ${side === "CT" ? "CT · Silver" : side === "T" ? "T · Gold" : "Dragon"} placed</b><span>Good luck — demo bets, nothing leaves your browser.</span>`,
        2400
      );
    };

    // Place Bet headers (desktop + mobile)
    for (const h of Array.from(
      root.querySelectorAll(
        ".Card_header__nyOYy, .CardForMobile_headerMobile__J1k74"
      )
    )) {
      const side = sideFromHeader(h);
      if (!side) continue;
      (h as HTMLElement).style.cursor = "pointer";
      on(
        h,
        "click",
        ((ev: Event) => {
          ev.preventDefault();
          placeBet(side);
        }) as EventListener
      );
    }

    /* ── bet modifiers (+0.1, 1/2, +1, +10, +100, 2X, Max) ── */
    const modScope =
      betInput?.closest("div.mt-6") || betInput?.parentElement || root;
    modScope
      .querySelectorAll("button")
      .forEach((b) => {
        const label = (b.textContent || "").trim();
        if (
          !["+0.1", "1/2", "+1", "+10", "+100", "2X", "Max"].includes(label)
        )
          return;
        on(
          b,
          "click",
          ((ev: Event) => {
            ev.preventDefault();
            if (!betInput) return;
            const cur = Number(betInput.value) || 0;
            if (label === "1/2")
              betInput.value = String(Math.max(0.1, cur / 2));
            else if (label === "+0.1")
              betInput.value = String(Math.round((cur + 0.1) * 100) / 100);
            else if (label === "+1")
              betInput.value = String(cur + 1);
            else if (label === "+10")
              betInput.value = String(cur + 10);
            else if (label === "+100")
              betInput.value = String(cur + 100);
            else if (label === "2X")
              betInput.value = String(cur * 2 || 10);
            else if (label === "Max") betInput.value = "1000";
          }) as EventListener
        );
      });

    /* ── volume toggle ── */
    const volImg = Array.from(root.querySelectorAll("img")).find((i) =>
      (i.getAttribute("src") || "").includes("volume-high")
    );
    const paintVol = () => {
      if (volImg instanceof HTMLElement)
        volImg.style.opacity = muted ? "0.35" : "1";
    };
    paintVol();
    if (volImg) {
      (volImg.parentElement || volImg).addEventListener("click", () => {
        muted = !muted;
        try {
          localStorage.setItem("roulette-muted", muted ? "1" : "0");
        } catch {
          /* noop */
        }
        paintVol();
        showBanner(
          muted
            ? `<b>Sound off</b>`
            : `<b>Sound on</b><span>Spin ticks + win chime enabled.</span>`,
          1800
        );
      });
    }

    /* ── main loop: betting → rolling → result ── */
    let raf = 0;
    let lastT = performance.now();
    let phaseT = 0; // seconds in phase
    let bettingLen = 2.5; // first round starts fast so visitors see a roll
    let rollFrom = 0;
    let rollTo = 0;
    let rollDur = 6.5;
    let winner: Side = "T";
    let lastCenter: Card | null = null;
    let roundNo = 0;

    const clearWinnerGlow = () => {
      for (const c of cards) {
        c.stage.setAttribute("data-winning", "false");
        const img = c.stage.querySelector("img");
        if (img instanceof HTMLElement) {
          img.style.filter = "";
          img.style.opacity = "";
        }
      }
    };

    const startRoll = () => {
      phase = "rolling";
      phaseT = 0;
      winner = pickWinner();
      roundNo += 1;
      clearWinnerGlow();
      sounds.lock();
      if (hashEl) hashEl.textContent = randomHex(64);
      // choose the nearest winning-side card at least 3 laps ahead
      const cx = centerX();
      let bestOff = Infinity;
      for (const c of cards) {
        if (c.side !== winner) continue;
        const pos = posOf(c);
        const d = ((((pos - cx) % totalW) + totalW) % totalW) + 3 * totalW;
        const target = off + d;
        if (target < bestOff) bestOff = target;
      }
      rollFrom = off;
      rollTo = bestOff;
      rollDur = 6.2 + Math.random() * 1.2;
      lastCenter = null;
    };

    const finishRoll = () => {
      phase = "result";
      phaseT = 0;
      off = rollTo;
      const winCard = paint();
      if (winCard) {
        winCard.stage.setAttribute("data-winning", "true");
        const img = winCard.stage.querySelector("img");
        if (img instanceof HTMLElement) {
          img.style.opacity = "1";
          const glow =
            winner === "Dragon"
              ? "drop-shadow(0 0 26px rgba(211,44,230,.8))"
              : winner === "T"
                ? "drop-shadow(0 0 26px rgba(228,174,57,.8))"
                : "drop-shadow(0 0 26px rgba(176,195,217,.8))";
          img.style.filter = glow;
        }
      }
      // last-100 counters
      try {
        for (const b of last100Btns) {
          const html = b.innerHTML || "";
          const isW =
            (winner === "T" && html.includes("gold")) ||
            (winner === "CT" && html.includes("silver")) ||
            (winner === "Dragon" && html.includes("dragon"));
          if (!isW) continue;
          const span = b.querySelector("span");
          if (span) {
            const n = parseInt(span.textContent || "0", 10);
            if (Number.isFinite(n)) span.textContent = String(n + 1);
          }
        }
      } catch {
        /* noop */
      }
      const myBet = bets[winner];
      const profit = myBet * PAYOUT[winner];
      if (myBet > 0) sounds.win();
      const names: Record<Side, string> = {
        CT: "CT · Silver",
        T: "T · Gold",
        Dragon: "Dragon",
      };
      showBanner(
        myBet > 0
          ? `<b>${names[winner]} wins — paid ${profit.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })} (16X on Dragon, 2X on sides)</b><span>Round #${roundNo} · demo payout, nothing leaves your browser.</span>`
          : `<b>${names[winner]} wins</b><span>Place a demo bet on a side card to play the next round.</span>`
      );
      bets.CT = 0;
      bets.T = 0;
      bets.Dragon = 0;
      // clear demo rows, keep snapshot rows
      root
        .querySelectorAll("[data-demo-bet]")
        .forEach((n) => n.remove());
      refreshTotals();
    };

    const easeOutQuart = (p: number): number => 1 - Math.pow(1 - p, 4);

    const loop = (now: number) => {
      const dt = Math.min(0.1, (now - lastT) / 1000);
      lastT = now;
      phaseT += dt;
      if (phase === "betting") {
        off += 34 * dt; // slow idle drift like the live page
        paint();
        const left = Math.max(0, bettingLen - phaseT);
        setTimer("Betting", left, 1 - left / bettingLen);
        if (left <= 0) startRoll();
      } else if (phase === "rolling") {
        const p = Math.min(1, phaseT / rollDur);
        off = rollFrom + (rollTo - rollFrom) * easeOutQuart(p);
        const center = paint();
        if (center && center !== lastCenter) {
          lastCenter = center;
          sounds.tick();
        }
        const left = Math.max(0, rollDur - phaseT);
        setTimer("Rolling", left, left / rollDur);
        if (p >= 1) finishRoll();
      } else {
        paint();
        setTimer("Rolling", 0, 0);
        if (phaseT >= 4.5) {
          phase = "betting";
          phaseT = 0;
          bettingLen = 8;
          clearWinnerGlow();
        }
      }
      raf = requestAnimationFrame(loop);
    };
    paint();
    raf = requestAnimationFrame(loop);
    cleanups.push(() => cancelAnimationFrame(raf));

    // unlock audio on first interaction (autoplay policy)
    on(
      document,
      "pointerdown",
      (() => {
        try {
          const AC =
            window.AudioContext ||
            (
              window as unknown as {
                webkitAudioContext?: typeof AudioContext;
              }
            ).webkitAudioContext;
        } catch {
          /* noop */
        }
      }) as EventListener,
      { once: true } as AddEventListenerOptions
    );

    return () => {
      cleanups.forEach((fn) => {
        try {
          fn();
        } catch {
          /* noop */
        }
      });
    };
  }, []);

  return null;
}
